import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { usuariosService, Usuario } from '../src/services/usuariosService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { useAuth } from '../src/contexts/AuthContext';

interface UsuariosProps {
  onClose: () => void;
}

type SortDirection = 'asc' | 'desc';
type SortableUsuarioKey = 'nombreUsuario' | 'telefono' | 'fechaCreacion' | 'lastLogin';

interface UsuarioFormData {
  telefono: string;
  password: string;
  nombreUsuario: string;
  activo: boolean;
  imagenUrl: string;
  empresaId: number | null;
}

const Usuarios: React.FC<UsuariosProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';
  
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null);
  const [formData, setFormData] = useState<UsuarioFormData>({
    telefono: '',
    password: '',
    nombreUsuario: '',
    activo: true,
    imagenUrl: '',
    empresaId: null,
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [selectedEmpresaFilter, setSelectedEmpresaFilter] = useState<number | null>(user?.empresaId ?? null);
  const [sortConfig, setSortConfig] = useState<{ key: SortableUsuarioKey; direction: SortDirection } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Inject CSS styles for modal animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.001); opacity: 0; }
      }
      @keyframes crecer {
        0% { transform: scale(0.001); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // Update selectedEmpresaFilter when user becomes available
  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setSelectedEmpresaFilter(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usuariosData, empresasData] = await Promise.all([
        usuariosService.getAll(),
        empresasService.getAll()
      ]);
      setUsuarios(usuariosData);
      setEmpresas(empresasData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortableUsuarioKey) => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredUsuarios = useMemo(() => {
    const loweredTerm = searchTerm.toLowerCase();
    let filtered = usuarios.filter(u => {
      const matchSearch = u.nombreUsuario.toLowerCase().includes(loweredTerm) ||
                          u.telefono.toLowerCase().includes(loweredTerm);
      const matchEmpresa = selectedEmpresaFilter === null || u.empresaId === selectedEmpresaFilter;
      return matchSearch && matchEmpresa;
    });

    if (sortConfig) {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (sortConfig.key === 'fechaCreacion' || sortConfig.key === 'lastLogin') {
          const aDate = aValue ? new Date(aValue as string).getTime() : 0;
          const bDate = bValue ? new Date(bValue as string).getTime() : 0;
          return (aDate - bDate) * direction;
        }
        if ((aValue ?? '') < (bValue ?? '')) return -1 * direction;
        if ((aValue ?? '') > (bValue ?? '')) return 1 * direction;
        return 0;
      });
    }
    return filtered;
  }, [usuarios, searchTerm, selectedEmpresaFilter, sortConfig]);

  const getEmpresaNombre = (empresaId: number | null) => {
    if (!empresaId) return '—';
    const empresa = empresas.find(e => e.empresaId === empresaId);
    return empresa?.nombreEmpresa || '—';
  };

  const handleCreate = () => {
    setEditingUsuario(null);
    // Default to selectedEmpresaFilter, or user's empresaId for non-SuperAdmin
    const defaultEmpresaId = isSuperAdmin 
      ? (selectedEmpresaFilter ?? empresas.find(e => e.activa)?.empresaId ?? null)
      : (user?.empresaId ?? null);
    setFormData({
      telefono: '',
      password: '',
      nombreUsuario: '',
      activo: true,
      imagenUrl: '',
      empresaId: defaultEmpresaId,
    });
    setShowPassword(false);
    setSelectedFile(null);
    setImagePreview(null);
    setShowForm(true);
  };

  const getFullImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
    return `${apiBase}${url}`;
  };

  const handleEdit = (usuario: Usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      telefono: usuario.telefono,
      password: '',
      nombreUsuario: usuario.nombreUsuario,
      activo: usuario.activo,
      imagenUrl: usuario.imagenUrl || '',
      empresaId: usuario.empresaId,
    });
    setShowPassword(false);
    setSelectedFile(null);
    setImagePreview(getFullImageUrl(usuario.imagenUrl));
    setShowForm(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowForm(false);
      setIsModalClosing(false);
      setEditingUsuario(null);
    }, 300);
  };

  const handleDelete = async (usuario: Usuario) => {
    const res = await Swal.fire({
      title: '¿Desactivar usuario?',
      text: `El usuario "${usuario.nombreUsuario}" será marcado como inactivo.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
    if (!res.isConfirmed) return;
    try {
      await usuariosService.delete(usuario.usuarioId);
      setUsuarios(prev => prev.map(u => u.usuarioId === usuario.usuarioId ? { ...u, activo: false } : u));
      await Swal.fire({
        title: 'Usuario desactivado',
        text: 'Se desactivó correctamente.',
        icon: 'success',
        background: 'rgba(15,23,42,0.85)',
        color: '#fff',
        confirmButtonColor: '#3b82f6',
        backdrop: false,
        timer: 2000,
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al desactivar';
      setError(message);
      Swal.fire({ title: 'Error', text: message, icon: 'error', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#ef4444', backdrop: false, customClass: { popup: 'rounded-2xl' } });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      let savedUsuario: Usuario;
      if (editingUsuario) {
        savedUsuario = await usuariosService.update(editingUsuario.usuarioId, {
          telefono: formData.telefono,
          password: formData.password || null,
          nombreUsuario: formData.nombreUsuario,
          activo: formData.activo,
          imagenUrl: formData.imagenUrl || null,
          empresaId: formData.empresaId,
        });
      } else {
        savedUsuario = await usuariosService.create({
          telefono: formData.telefono,
          password: formData.password,
          nombreUsuario: formData.nombreUsuario,
          activo: formData.activo,
          imagenUrl: formData.imagenUrl || null,
          empresaId: formData.empresaId,
        });
      }

      if (selectedFile) {
        const uploadResult = await usuariosService.uploadImage(savedUsuario.usuarioId, selectedFile);
        savedUsuario = { ...savedUsuario, imagenUrl: uploadResult.imagenUrl };
      }

      if (editingUsuario) {
        setUsuarios(prev => prev.map(u => u.usuarioId === savedUsuario.usuarioId ? savedUsuario : u));
        await Swal.fire({
          title: 'Usuario actualizado',
          text: 'Los datos se guardaron correctamente.',
          icon: 'success',
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonColor: '#3b82f6',
          backdrop: false,
          timer: 2000,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      } else {
        setUsuarios(prev => [savedUsuario, ...prev]);
        await Swal.fire({
          title: 'Usuario creado',
          text: 'El usuario fue registrado correctamente.',
          icon: 'success',
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonColor: '#3b82f6',
          backdrop: false,
          timer: 2000,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      }
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al guardar';
      setError(message);
      Swal.fire({ title: 'Error', text: message, icon: 'error', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#ef4444', backdrop: false, customClass: { popup: 'rounded-2xl' } });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 flex items-start justify-center bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="w-full max-w-6xl px-2 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors">
              <span className="material-symbols-outlined">arrow_back</span>
              Volver
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-2xl">group</span>
            <h1 className="text-2xl  text-secondary dark:text-white">Usuarios</h1>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {!showForm ? (
            <>
              {/* Toolbar */}
              <div className="bg-[#307CAF] text-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {isSuperAdmin && (
                        <div>
                          <label className="block text-xs font-semibold uppercase tracking-wide mb-1">Empresa</label>
                          <select
                            value={selectedEmpresaFilter ?? ''}
                            onChange={(e) => setSelectedEmpresaFilter(e.target.value ? Number(e.target.value) : null)}
                            className="w-full rounded-full border border-transparent bg-white dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value="">Todas</option>
                            {empresas.filter(e => e.activa).map(empresa => (
                              <option key={empresa.empresaId} value={empresa.empresaId}>
                                {empresa.nombreEmpresa}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide mb-1">Buscar</label>
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                          <input
                            type="text"
                            placeholder="Buscar por nombre o teléfono..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-transparent rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>{filteredUsuarios.length} usuarios</span>
                    <button onClick={loadData} className="inline-flex items-center gap-1 px-3 py-1 border border-white/20 rounded-full hover:bg-white/10 transition" title="Recargar">
                      <span className="material-symbols-outlined text-base">refresh</span>
                      Recargar
                    </button>
                    <button onClick={handleCreate} className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#307CAF] shadow hover:bg-slate-50 transition-colors">
                      <span className="material-symbols-outlined text-base">add</span>
                      Nuevo Usuario
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              {loading ? (
                <div className="p-12 text-center text-slate-500 dark:text-slate-400">Cargando usuarios...</div>
              ) : filteredUsuarios.length === 0 ? (
                <div className="p-12 text-center text-slate-500 dark:text-slate-400">No se encontraron usuarios con ese criterio.</div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-sm">
                      <thead className="bg-[#307CAF] text-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th onClick={() => handleSort('nombreUsuario')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Usuario
                              {sortConfig?.key === 'nombreUsuario' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th onClick={() => handleSort('telefono')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Teléfono
                              {sortConfig?.key === 'telefono' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Empresa</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Estado</th>
                          <th onClick={() => handleSort('fechaCreacion')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Fecha de registro
                              {sortConfig?.key === 'fechaCreacion' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th onClick={() => handleSort('lastLogin')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Último acceso
                              {sortConfig?.key === 'lastLogin' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {filteredUsuarios.map((usuario) => (
                          <tr key={usuario.usuarioId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {usuario.imagenUrl ? (
                                  <img
                                    src={getFullImageUrl(usuario.imagenUrl)}
                                    alt={usuario.nombreUsuario}
                                    className="h-10 w-10 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
                                    onError={(e) => { e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40"%3E%3Crect width="40" height="40" fill="%23e2e8f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%2394a3b8"%3E?%3C/text%3E%3C/svg%3E'; }}
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-slate-500 dark:text-slate-400">person</span>
                                  </div>
                                )}
                                <div>
                                  <p className="font-semibold text-slate-900 dark:text-white">{usuario.nombreUsuario}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">ID #{usuario.usuarioId}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">{usuario.telefono}</td>
                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">{getEmpresaNombre(usuario.empresaId)}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${usuario.activo ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'}`}>
                                <span className="material-symbols-outlined text-sm">{usuario.activo ? 'verified' : 'visibility_off'}</span>
                                {usuario.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{formatDate(usuario.fechaCreacion)}</td>
                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{usuario.lastLogin ? formatDate(usuario.lastLogin) : '—'}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleEdit(usuario)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50">
                                  <span className="material-symbols-outlined text-sm">edit</span>
                                  Editar
                                </button>
                                <button onClick={() => handleDelete(usuario)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  <span className="material-symbols-outlined text-sm">delete</span>
                                  Inactivar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Form Modal */
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
              <div className={`relative w-full max-w-md transform rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl transition-all ${isModalClosing ? 'animate-[disminuir_0.3s_ease-in_forwards]' : 'animate-[crecer_0.3s_ease-out_forwards]'}`}>
                <div className="rounded-2xl backdrop-blur-sm bg-white/50 dark:bg-slate-800 p-6 shadow-xl dark:border-slate-700 max-h-[90vh] overflow-y-auto">
                  <button onClick={closeModal} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
                    <span className="material-symbols-outlined">close</span>
                  </button>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingUsuario ? 'Editar usuario' : 'Crear usuario'}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Completa los datos base del usuario.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {isSuperAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa</label>
                        <select
                          value={formData.empresaId ?? ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, empresaId: e.target.value ? Number(e.target.value) : null }))}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Sin empresa</option>
                          {empresas.filter(e => e.activa).map((empresa) => (
                            <option key={empresa.empresaId} value={empresa.empresaId}>
                              {empresa.nombreEmpresa}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre de usuario</label>
                      <input
                        type="text"
                        value={formData.nombreUsuario}
                        maxLength={100}
                        onChange={(e) => setFormData(prev => ({ ...prev, nombreUsuario: e.target.value }))}
                        required
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Teléfono</label>
                      <input
                        type="tel"
                        value={formData.telefono}
                        maxLength={15}
                        onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value.replace(/\s+/g, '') }))}
                        required
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">
                        Contraseña {editingUsuario && <span className="text-xs text-slate-500">(dejar en blanco para conservar)</span>}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          minLength={editingUsuario ? 0 : 3}
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          required={!editingUsuario}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 pr-10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-[#307CAF] dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          <span className="material-symbols-outlined text-lg">{showPassword ? 'visibility_off' : 'visibility'}</span>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Imagen de perfil (opcional)</label>
                      <div className="flex items-center gap-4">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700" />
                        ) : (
                          <div className="h-16 w-16 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                            <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-2xl">person</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
                            <span className="material-symbols-outlined text-base">upload</span>
                            {selectedFile ? selectedFile.name : 'Seleccionar imagen'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">JPG, PNG o WEBP. Máx 5 MB.</p>
                        </div>
                      </div>
                    </div>
                    {editingUsuario && (
                      <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Estado</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Controla si el usuario puede iniciar sesión.</p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer">
                          <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.activo ? 'Activo' : 'Inactivo'}</span>
                          <input type="checkbox" className="sr-only" checked={formData.activo} onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))} />
                          <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.activo ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                            <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.activo ? 'translate-x-5' : ''}`} />
                          </span>
                        </label>
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button type="button" className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50" onClick={closeModal}>
                        Cancelar
                      </button>
                      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-hover disabled:opacity-60">
                        {saving ? (
                          <>
                            <span className="material-symbols-outlined animate-spin">progress_activity</span>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined">save</span>
                            Guardar
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Usuarios;
